export const ADMIN_LOGIN_PATH = '/login';
export const ADMIN_LOGOUT_PATH = '/logout/';
export const ADMIN_ACCESS_DENIED_REASON = 'access_denied';
export const ADMIN_ACCESS_DENIED_MESSAGE =
  'This account does not have access. Try another approved identity.';

export const ADMIN_SSO_PROVIDERS = {
  apple: 'AppleOAuth',
  google: 'GoogleOAuth',
} as const;

export type AdminSsoProvider = (typeof ADMIN_SSO_PROVIDERS)[keyof typeof ADMIN_SSO_PROVIDERS];

export function adminLogoutReturnUrl(origin: string) {
  return new URL(ADMIN_LOGOUT_PATH, origin).toString();
}

export function adminAccessDeniedLogoutUrl(origin: string) {
  const url = new URL(ADMIN_LOGOUT_PATH, origin);
  url.searchParams.set('reason', ADMIN_ACCESS_DENIED_REASON);
  return url.toString();
}

export function adminLogoutDestination(currentUrl: string, unauthorizedRedirectUrl: string) {
  const current = new URL(currentUrl);
  if (current.searchParams.get('reason') === ADMIN_ACCESS_DENIED_REASON) {
    const login = new URL(ADMIN_LOGIN_PATH, current.origin);
    login.searchParams.set('error', ADMIN_ACCESS_DENIED_REASON);
    return login.toString();
  }
  return unauthorizedRedirectUrl;
}

export function adminLoginError(currentUrl: string) {
  const current = new URL(currentUrl);
  return current.searchParams.get('error') === ADMIN_ACCESS_DENIED_REASON
    ? ADMIN_ACCESS_DENIED_MESSAGE
    : null;
}

export async function completeRejectedSession(
  signOut: (options: { navigate: false }) => Promise<void>,
  origin: string,
  navigate: (url: string) => void,
  timeoutMs = 1_500,
) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      signOut({ navigate: false }).catch(() => undefined),
      new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
    navigate(adminAccessDeniedLogoutUrl(origin));
  }
}

export function isAdminLogoutPath(pathname: string) {
  return pathname === ADMIN_LOGOUT_PATH || pathname === '/logout';
}

export function adminSsoAuthorizationUrl(authKitUrl: string, provider: AdminSsoProvider) {
  const url = new URL(authKitUrl);
  if (!url.pathname.endsWith('/user_management/authorize')) {
    throw new Error('AuthKit returned an unexpected authorization URL');
  }
  url.searchParams.set('provider', provider);
  url.searchParams.delete('screen_hint');
  return url.toString();
}

export function isAdminSsoAuthenticationMethod(value: unknown): value is AdminSsoProvider {
  return Object.values(ADMIN_SSO_PROVIDERS).includes(value as AdminSsoProvider);
}
